import React from 'react';

interface Props {
  /** Sample manager row from /api/ai-search/sample-entity. */
  manager: any | null;
  /** Columns currently included in the active filter group. */
  selectedColumns: Set<string>;
  /** Toggles ALL columns of a clicked cell in the active filter group. */
  onToggleColumns: (columns: string[]) => void;
}

const truncate = (s: string | null | undefined, n: number): string => {
  if (!s) return '';
  return s.length > n ? s.slice(0, n).trimEnd() + '…' : s;
};

const formatColumnLabel = (col: string): string =>
  col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatArray = (v: any): string => {
  if (!v) return '';
  if (Array.isArray(v)) return v.filter(Boolean).join(', ');
  if (typeof v === 'string' && v.startsWith('{')) return v.replace(/[{}]/g, '').split(',').join(', ');
  if (typeof v === 'string' && v.startsWith('[')) {
    try { return JSON.parse(v).filter(Boolean).join(', '); } catch { return v; }
  }
  return String(v);
};

const PickableCell: React.FC<{
  columns: string[];
  selectedColumns: Set<string>;
  onToggleColumns: (cols: string[]) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ columns, selectedColumns, onToggleColumns, children, className }) => {
  const allSelected = columns.length > 0 && columns.every(c => selectedColumns.has(c));
  const someSelected = !allSelected && columns.some(c => selectedColumns.has(c));

  const stateClass = allSelected
    ? 'border-amber-400 bg-amber-50/70 ring-1 ring-amber-200'
    : someSelected
      ? 'border-amber-300 bg-amber-50/40'
      : 'border-transparent hover:border-blue-300 hover:bg-blue-50/40';

  const label = columns.map(formatColumnLabel).join(' · ');

  return (
    <button
      type="button"
      onClick={() => onToggleColumns(columns)}
      className={`group relative w-full text-left rounded-lg border-2 transition-colors p-2 ${stateClass} ${className || ''}`}
      title={`${allSelected ? 'Click to remove' : 'Click to add'}: ${columns.join(', ')}`}
      aria-pressed={allSelected}
    >
      <span
        className={`absolute top-1 right-1 px-1.5 py-0.5 text-[10px] font-medium rounded transition-opacity pointer-events-none ${
          allSelected
            ? 'bg-amber-200 text-amber-900 opacity-100'
            : someSelected
              ? 'bg-amber-100 text-amber-800 opacity-100'
              : 'bg-gray-700 text-white opacity-0 group-hover:opacity-90'
        }`}
      >
        {label}
      </span>
      {children}
    </button>
  );
};

const Empty: React.FC = () => <span className="text-gray-400 italic">—</span>;

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5 px-2">{children}</h4>
);

const ManagerPickerSchematic: React.FC<Props> = ({ manager, selectedColumns, onToggleColumns }) => {
  if (!manager) {
    return (
      <p className="text-xs text-gray-500 italic">
        No sample hiring manager available — add managers to the project to see the visual picker.
      </p>
    );
  }

  const m = manager;
  const sectionProps = { selectedColumns, onToggleColumns };

  return (
    <div className="space-y-3">
      {/* Header — name + job_title + org */}
      <div>
        <PickableCell columns={['name']} {...sectionProps}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {(m.name?.[0] || '?').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{m.name || <Empty />}</p>
            </div>
          </div>
        </PickableCell>

        <PickableCell columns={['job_title']} {...sectionProps} className="mt-1">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Job title</p>
          <p className="text-xs text-gray-700 truncate">{m.job_title || <Empty />}</p>
        </PickableCell>

        <PickableCell columns={['org']} {...sectionProps} className="mt-1">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Organisation</p>
          <p className="text-xs text-gray-700 truncate">{m.org || <Empty />}</p>
        </PickableCell>
      </div>

      {/* Role attributes */}
      <div>
        <SectionHeader>Role attributes</SectionHeader>
        <div className="grid grid-cols-2 gap-1">
          <PickableCell columns={['seniority_level']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Seniority</p>
            <p className="text-xs text-gray-700 truncate">{m.seniority_level || <Empty />}</p>
          </PickableCell>
          <PickableCell columns={['department']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Department</p>
            <p className="text-xs text-gray-700 truncate">{m.department || <Empty />}</p>
          </PickableCell>
        </div>

        <PickableCell columns={['location']} {...sectionProps} className="mt-1">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Location</p>
          <p className="text-xs text-gray-700 truncate">{m.location || <Empty />}</p>
        </PickableCell>

        <PickableCell columns={['tags']} {...sectionProps} className="mt-1">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Tags</p>
          <p className="text-xs text-gray-700">{formatArray(m.tags) || <Empty />}</p>
        </PickableCell>
      </div>

      {/* Profile text */}
      <div>
        <SectionHeader>Profile</SectionHeader>
        <PickableCell columns={['headline']} {...sectionProps}>
          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Headline</p>
          <p className="text-xs text-gray-700">{m.headline || <Empty />}</p>
        </PickableCell>

        <PickableCell columns={['summary']} {...sectionProps} className="mt-1">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Summary</p>
          <p className="text-xs text-gray-700 whitespace-pre-line">
            {m.summary ? truncate(m.summary, 320) : <Empty />}
          </p>
        </PickableCell>
      </div>
    </div>
  );
};

export const MANAGER_PICKER_COLUMNS: string[] = [
  'name', 'job_title', 'org',
  'seniority_level', 'department', 'location', 'tags',
  'headline', 'summary',
];

export const MANAGER_LONG_TAIL_COLUMNS: string[] = [
  'name', 'job_title', 'org',
  'seniority_level', 'department', 'location', 'tags',
  'headline', 'summary',
  // No long-tail manager fields beyond the main picker today; kept for parity.
];

export default ManagerPickerSchematic;
